import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CoinPackageDefinition,
  FurnitureDefinition,
} from "../../shared/types";
import type { Session } from "../lib/api";
import {
  buyFurniture,
  fetchCoinPackages,
  fetchFurnitureCatalog,
  fetchFurnitureInventory,
  purchaseCoinPackage,
  type FurnitureInventoryEntry,
} from "../lib/api";

export function useEconomy(session: Session | null) {
  const [coins, setCoins] = useState(0);
  const [packages, setPackages] = useState<CoinPackageDefinition[]>([]);
  const [catalog, setCatalog] = useState<FurnitureDefinition[]>([]);
  const [inventory, setInventory] = useState<FurnitureInventoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const inventoryById = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of inventory) {
      const prev = totals.get(entry.furnitureId) ?? 0;
      totals.set(entry.furnitureId, prev + Math.max(0, entry.quantity));
    }
    return totals;
  }, [inventory]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextPackages, nextCatalog] = await Promise.all([
        fetchCoinPackages(),
        fetchFurnitureCatalog(session),
      ]);
      setPackages(nextPackages);
      setCatalog(nextCatalog);
      if (!session) {
        setCoins(0);
        setInventory([]);
        return;
      }
      const nextInventory = await fetchFurnitureInventory(session);
      setCoins(nextInventory.coins);
      setInventory(nextInventory.inventory);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshInventory = useCallback(async () => {
    if (!session) {
      setCoins(0);
      setInventory([]);
      return;
    }
    const nextInventory = await fetchFurnitureInventory(session);
    setCoins(nextInventory.coins);
    setInventory(nextInventory.inventory);
  }, [session]);

  const purchaseCoins = useCallback(
    async (packageId: string) => {
      if (!session) {
        throw new Error("Not connected.");
      }
      const pack = packages.find((candidate) => candidate.id === packageId);
      if (!pack) {
        throw new Error("Unknown coin package.");
      }
      // DEBUG: skip wallet transaction, use free top-up
      const result = await purchaseCoinPackage(
        session,
        packageId,
        "debug_free"
      );
      setCoins(result.coins);
      return result;
    },
    [packages, session]
  );

  const purchaseFurniture = useCallback(
    async (furnitureId: string, quantity = 1, colorVariantId?: string) => {
      if (!session) {
        throw new Error("Not connected.");
      }
      const result = await buyFurniture(
        session,
        furnitureId,
        quantity,
        colorVariantId
      );
      await refreshInventory();
      return result;
    },
    [refreshInventory, session]
  );

  return {
    coins,
    packages,
    catalog,
    inventory,
    inventoryById,
    loading,
    load,
    refreshInventory,
    purchaseCoins,
    purchaseFurniture,
  };
}
